import { compose, reverse, sortBy } from 'lodash/fp';
import React from 'react';
import Alert from '../../components/Alert';
import Button from '../../components/Button';
import Loader from '../../components/Loader';
import { useUpgradeModal } from '../../hooks/useUpgradeModal';
import { ChurnData } from '../../types/dashboard';
import { isPaidPlan, UserPlan } from '../../utils/plans';
import { toFriendlyName } from '../../utils/strings';
import { formatDatePretty } from '../../utils/dates';

const sortByTotal = compose(reverse, sortBy<ChurnData>(['month', 'total']));

interface Props {
    data: ChurnData[] | undefined;
    currentPlan: UserPlan | undefined;
}

export default function ChurnChart({ data: churnData, currentPlan }: Props) {
    const promptUpgrade = currentPlan && !isPaidPlan(currentPlan);
    return (
        <div className="w-full mt-12">
            <p className="text-xl pb-3 flex items-center">Churn Reasons</p>
            <div className="bg-white overflow-auto relative">
                {promptUpgrade && <UpgradePrompt />}
                {!promptUpgrade && <Table data={churnData} />}
            </div>
        </div>
    );
}

function Table(props: Pick<Props, 'data'>) {
    const { data: churnData } = props;
    if (!churnData) {
        return <Loader full={false} />;
    } else if (typeof churnData === 'string') {
        return <Alert title="Error" body="Something went wrong please try again" />;
    }
    return (
        <>
            {typeof churnData === 'object' && (
                <table className="min-w-full bg-white">
                    <thead className="bg-blue-800 text-white">
                        <tr>
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Month</th>
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Reason</th>
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Total</th>
                            <th className="text-left py-3 px-4 uppercase font-semibold text-sm">Percent</th>
                            <th className="w-1/3 text-left py-3 px-4 uppercase font-semibold text-sm"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortByTotal(churnData).map(({ month, reason, total, percentage }) => (
                            <tr key={`${month}-${reason}`}>
                                <td className="text-left py-3 px-4">{formatDatePretty(new Date(month))}</td>
                                <td className="text-left py-3 px-4">{toFriendlyName(reason)}</td>
                                <td className="text-left py-3 px-4">{total}</td>
                                <td className="text-left py-3 px-4">{percentage}%</td>
                            </tr>
                        ))}
                        <tr className="border-t font-bold">
                            <td className="text-left py-3 px-4"></td>
                            <td className="text-left py-3 px-4"></td>
                            <td className="text-left py-3 px-4">
                                {churnData.reduce((prev, next) => prev + next.total, 0)}
                            </td>
                            <td className="text-left py-3 px-4"></td>
                        </tr>
                    </tbody>
                </table>
            )}
        </>
    );
}

function UpgradePrompt() {
    const [UpgradeModal, showUpgradePopup] = useUpgradeModal();
    const handleClick = () => showUpgradePopup();

    return (
        <div className="flex justify-center py-8">
            <Button kind="secondary" onClick={handleClick}>
                Upgrade to see Churn Reasons
            </Button>
            <UpgradeModal />
        </div>
    );
}
